import { useCallback, useState } from 'react';
import {
  Button,
  Stack,
  TextInput,
  Title,
  Text,
  type StackProps,
  Group,
  Loader,
  Anchor,
  Divider,
  useMantineTheme
} from '@mantine/core';

import { SyncConfigItem } from '../../../components';
import type { StorageProviderConfig } from '../../../types/apiTypes';
import useEnsisMutation from '../../../hooks/useEnsisMutation';
import { formatDateTimeFromString } from '../../../utils/stringUtils';
import { DEFAULT_FAILURE_MESSAGE, showFailureNotification } from '../../../utils/mantineUtils';

export interface CredentialsFormValues {
  clientId: string
  tenantId: string
  clientSecret: string
}

type Props = {
  storageProviderConfig: StorageProviderConfig
  canEdit: boolean
} & StackProps;

const SYNC_CONFIG_ERROR_TEXT = 'Please enter a valid location';
const DUPLICATE_URL_ERROR_TEXT = 'Location URLs must be unique';
const SHAREPOINT_INSTRUCTIONS_URL = 'https://ensis-ai.gitbook.io/ensis-documentation/product-guides' +
  '/settings-and-admin/sharepoint';

const SyncConfigsForm: React.FC<Props> = ({
  canEdit,
  storageProviderConfig,
  ...stackProps
}: Props) => {
  const syncConfigsExist = (storageProviderConfig?.sync_configs ?? []).length > 0;
  const initialNewUrls = syncConfigsExist ? [] : [''];
  const [newUrls, setNewUrls] = useState<string[]>(initialNewUrls);
  const [errors, setErrors] = useState<Set<number>>(new Set());
  const { colors } = useMantineTheme();
  const resetForm = useCallback(() => {
    setNewUrls(initialNewUrls);
    setErrors(new Set());
  }, [initialNewUrls]);

  const triggerManualSyncMutation = useEnsisMutation(
    `/app/storage-provider-configs/${storageProviderConfig?.uid}/start-sync`,
    {
      requestType: 'post',
      queryKeysToInvalidate: ['/app/storage-provider-configs'],
      awaitRefetch: true,
      successMessage: 'Sync started'
    }
  );

  const validateSyncConfigMutation = useEnsisMutation(
    '/app/storage-provider-sync-configs/validate',
    {
      requestType: 'post',
      showSuccessMessage: false,
      showFailureMessage: false
    }
  );

  const createSyncConfigsMutation = useEnsisMutation(
    '/app/storage-provider-sync-configs/',
    {
      requestType: 'post',
      queryKeysToInvalidate: ['/app/storage-provider-configs'],
      awaitRefetch: true,
      successMessage: 'Locations added',
      onSuccess: resetForm,
      onError: (error) => {
        let errorMessage = DEFAULT_FAILURE_MESSAGE;
        if (error?.response?.status === 409) {
          errorMessage = 'These locations contain overlapping folders. Locations must be unique.';
        }
        showFailureNotification(errorMessage);
      },
      showFailureMessage: false
    }
  );

  const handleClickAddAnother = useCallback(() => {
    setNewUrls([...newUrls, '']);
  }, [newUrls]);

  const handleUpdateNewUrl = useCallback((index: number, text: string) => {
    const updatedPaths = [...newUrls];
    updatedPaths.splice(index, 1, text);
    setNewUrls(updatedPaths);

    // Reset errors on field when updated
    const updatedErrors = new Set(errors);
    updatedErrors.delete(index);
    setErrors(updatedErrors);
  }, [newUrls, errors]);

  const handleTriggerSync = useCallback(() => {
    triggerManualSyncMutation.mutate({});
  }, [triggerManualSyncMutation.mutate]);

  const handleSubmit = useCallback(async () => {
    // Remove empty locations
    const nonEmptyUrls = newUrls.filter((newUrl) => newUrl !== '');
    setNewUrls(nonEmptyUrls);

    // Validate locations
    const newErrors = new Set<number>();
    for (const [i, url] of nonEmptyUrls.entries()) {
      try {
        await validateSyncConfigMutation.mutateAsync({
          storage_provider_config_uid: storageProviderConfig.uid ?? '',
          url
        });
      } catch {
        newErrors.add(i);
      }
    }

    if (newErrors.size > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await createSyncConfigsMutation.mutateAsync({
        storage_provider_config_uid: storageProviderConfig.uid ?? '',
        urls: nonEmptyUrls
      });
      await triggerManualSyncMutation.mutateAsync({});
    } catch {

    }
  }, [
    newUrls,
    storageProviderConfig?.uid,
    triggerManualSyncMutation.mutateAsync,
    createSyncConfigsMutation.mutateAsync
  ]);

  const urlHasDuplicates = useCallback((newUrl: string) => {
    const existingSyncConfigUrls = storageProviderConfig?.sync_configs?.map((syncConfig) => (
      syncConfig.sync_config?.full_url
    )) ?? [];
    const allUrls = [...existingSyncConfigUrls, ...newUrls];
    return allUrls.filter((url) => url === newUrl).length > 1;
  }, [newUrls, storageProviderConfig?.sync_configs]);

  const duplicateUrls = new Set(newUrls.filter((url) => urlHasDuplicates(url)));

  const isLoading = createSyncConfigsMutation.isPending || validateSyncConfigMutation.isPending;
  const isSyncing = storageProviderConfig?.is_syncing ?? false;
  const addAnotherButtonDisabled = isSyncing || isLoading || !canEdit;
  const cancelButtonDisabled = newUrls.length === initialNewUrls.length || isLoading || isSyncing || !canEdit;
  const saveButtonDisabled = newUrls.length === 0 || newUrls.every((newUrl) => newUrl === '') ||
    isLoading || isSyncing || duplicateUrls.size > 0 || !canEdit;
  const hasLastSync = storageProviderConfig?.last_sync_completed_at !== undefined &&
    storageProviderConfig?.last_sync_completed_at != null;

  const syncingIndicator = (
    <Group gap={6}>
      <Loader size='xs' color={colors.gray[5]} />
      <Text fz='sm' c={colors.gray[6]}>
        Syncing Locations. This may take several minutes.
      </Text>
    </Group>
  );

  const completedSyncStatus = hasLastSync && (
    <Group gap={8} align='center'>
      <Text fz='sm' c={colors.gray[6]}>
        {`Last synced: ${formatDateTimeFromString(storageProviderConfig?.last_sync_completed_at ?? '')}`}
      </Text>
      <Button
        disabled={triggerManualSyncMutation.isPending}
        loading={triggerManualSyncMutation.isPending}
        p={0}
        variant='transparent'
        onClick={handleTriggerSync}
      >
        Sync Now
      </Button>
    </Group>
  );

  const syncStatus = isSyncing ? syncingIndicator : completedSyncStatus;

  return (
    <Stack {...stackProps} align='flex-start'>
      <Title order={6}>Locations</Title>
      <Text fz='sm'>
        {'Enter the full file path(s) of the SharePoint document libraries or folders you want to sync ' +
          'with Ensis. Click \'Save and Sync\' to begin syncing. Need help? '}
        <Anchor
          fz='sm'
          href={SHAREPOINT_INSTRUCTIONS_URL}
          target="_blank"
          underline='always'
          inherit
        >
          View instructions
        </Anchor>
      </Text>
      {syncStatus}
      <Stack w='100%'>
        {
          storageProviderConfig.sync_configs?.map((syncConfig) => (
            <Stack key={syncConfig.uid}>
              <SyncConfigItem
                canDelete={canEdit}
                syncConfig={syncConfig}
              />
              <Divider />
            </Stack>
          ))
        }
        {
          newUrls.map((newUrl, index) => {
            let errorText: string | undefined;
            if (errors.has(index)) {
              errorText = SYNC_CONFIG_ERROR_TEXT;
            } else if (duplicateUrls.has(newUrl)) {
              errorText = DUPLICATE_URL_ERROR_TEXT;
            }
            return (
              <TextInput
                key={index}
                disabled={isSyncing || isLoading || !canEdit}
                error={errorText}
                w={480}
                label='Document / Folder location'
                value={newUrl}
                onChange={(e) => { handleUpdateNewUrl(index, e.target.value); }}
              />
            );
          })
        }
      </Stack>
      {
        !addAnotherButtonDisabled && (
          <Button
            p={0}
            variant='transparent'
            onClick={handleClickAddAnother}
          >
            + Add another
          </Button>
        )
      }
      <Group>
        <Button
          variant='outline'
          disabled={cancelButtonDisabled}
          onClick={resetForm}
        >
          Cancel
        </Button>
        <Button
          loading={isLoading}
          disabled={saveButtonDisabled}
          onClick={() => { void handleSubmit(); }}
        >
          Save and Sync
        </Button>
      </Group>
    </Stack>
  );
};

export default SyncConfigsForm;
